import React, { useEffect } from "react";
import { SlideshowLightbox, initLightboxJS } from "lightbox.js-react";
import { Headline, ContentBlock, Paragraph } from "./ui";

const LighthouseGrid = ({
  images,
  className = "",
  showThumbnails = true,
  lightboxKey = "ch_3QMdj8JAthSyOIuA0GSfVViL",
  imageWidth = 800,
  imageCrop = "fill",
  imageGravity = "center",
  highQuality = false,
  // Header content props
  showHeader = false,
  title = "",
  subtitle = "",
  description = "",
}) => {

  useEffect(() => {

    if (images && images.length > 0) {
      // Small delay to ensure DOM is updated before initializing Lighthouse
      const timeoutId = setTimeout(() => {
        initLightboxJS(lightboxKey, "individual");
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [
    images,
    lightboxKey,
    JSON.stringify(
      images?.map((img) => (typeof img === "string" ? img : img.src))
    ),
  ]);

  if (!images || images.length === 0) {
    return null;
  }

  // Helper function to build Cloudinary URLs (similar to SmartImage logic)
  const buildImageUrl = (src) => {
    const cloudName = process.env.REACT_APP_CLOUDINARY_NAME;

    if (!cloudName) {
      console.warn("REACT_APP_CLOUDINARY_NAME not set");
      return src;
    }

    // If already a full URL, use as-is
    if (src.startsWith("http")) return src;

    // Clean public ID (remove leading slash if present)
    const publicId = src.startsWith("/") ? src.substring(1) : src;

    // Build URL with transformations
    const baseUrl = `https://res.cloudinary.com/${cloudName}/image/upload`;
    const transformations = [];

    if (imageWidth) transformations.push(`w_${imageWidth}`);
    if (imageCrop) transformations.push(`c_${imageCrop}`);
    if (imageGravity && imageCrop !== "scale")
      transformations.push(`g_${imageGravity}`);

    transformations.push("q_auto");
    if (highQuality) transformations.push("f_auto");

    const transformString = transformations.length
      ? `${transformations.join(",")}/`
      : "";

    return `${baseUrl}/${transformString}${publicId}`;
  };

  return (
    <div className={`w-full mx-auto ${className}`}>
      {showHeader && (
        <ContentBlock maxWidth="4xl" className="mx-auto">
          {title && (
            <Headline variant="h1" className="text-left mb-2" noSpacing={true}>
              {title}
            </Headline>
          )}
          {subtitle && (
            <Headline variant="h4" className="text-left mb-2">
              {subtitle}
            </Headline>
          )}
          {description && (
            <Paragraph variant="large" className="text-left sm:mb-24">
              {description}
            </Paragraph>
          )}
        </ContentBlock>
      )}
      <SlideshowLightbox
        key={`lightbox-${JSON.stringify(
          images?.map((img) => (typeof img === "string" ? img : img.src))
        )}`}
        className="grid gap-2 w-full grid-cols-2 md:grid-cols-3 lg:grid-cols-4 md:gap-3 lg:gap-3"
        showThumbnails={showThumbnails}
      >
        {images.map((image, index) => {
          // Handle both string arrays and object arrays
          const imageSrc = typeof image === "string" ? image : image.src;
          const imageAlt =
            typeof image === "string"
              ? `Image ${index + 1}`
              : image.alt || `Image ${index + 1}`;

          const finalImageUrl = buildImageUrl(imageSrc);


          return (
            <img
              key={index}
              className="w-full h-full object-cover  cursor-pointer transition-all duration-300 ease-in-out hover:-translate-y-0.1 hover:shadow-xl hover:scale-102 aspect-square  block"
              src={finalImageUrl}
              alt={imageAlt}
              onError={(e) =>
                console.error(
                  `❌ Image ${index} failed to load:`,
                  finalImageUrl,
                  e
                )
              }
            />
          );
        })}
      </SlideshowLightbox>
    </div>
  );
};

export default LighthouseGrid;
